/**
 * Email-verification landing — user clicks the link in the signup email,
 * we POST the token to /v1/auth/verify-email, then nudge them toward the
 * "wait for operator approval" message.
 */

import { Alert, Anchor, Container, Group, Loader, Paper, Stack, Text, Title } from "@mantine/core";
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { ApiError, api } from "../lib/api";

type State =
  | { kind: "loading" }
  | { kind: "verified"; awaiting_operator_approval: boolean }
  | { kind: "expired" }
  | { kind: "error"; detail: string };

export function VerifyEmailScreen() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const [state, setState] = useState<State>({ kind: "loading" });

  useEffect(() => {
    if (!token) {
      setState({ kind: "error", detail: "missing_token" });
      return;
    }
    (async () => {
      try {
        const r = await api.verifyEmail(token);
        setState({
          kind: "verified",
          awaiting_operator_approval: r.awaiting_operator_approval,
        });
      } catch (err) {
        if (err instanceof ApiError && err.detail === "verify_token_invalid_or_expired") {
          setState({ kind: "expired" });
        } else {
          setState({
            kind: "error",
            detail: err instanceof ApiError ? err.detail : "unexpected",
          });
        }
      }
    })();
  }, [token]);

  return (
    <Container size="xs" py="xl">
      <Paper p="lg" radius="md" withBorder>
        <Stack>
          <Title order={2}>אימות דוא"ל</Title>
          {state.kind === "loading" && (
            <Group>
              <Loader size="sm" />
              <Text>מאמת...</Text>
            </Group>
          )}
          {state.kind === "verified" && (
            <Alert color="green">
              <Text fw={500}>כתובת הדוא"ל אומתה.</Text>
              {state.awaiting_operator_approval ? (
                <Text size="sm" mt={6}>
                  כעת ממתינים לאישור של מפעיל המערכת. תקבל/י הודעת דוא"ל נוספת
                  כשהחשבון יאושר.
                </Text>
              ) : (
                <Text size="sm" mt={6}>
                  החשבון כבר אושר. את/ה יכול/ה להתחבר.
                </Text>
              )}
            </Alert>
          )}
          {state.kind === "expired" && (
            <Alert color="red">
              הקישור פג תוקף או כבר נוצל. אם עדיין לא קיבלת אישור, פנה/י למפעיל
              המערכת או הירשם/י מחדש.
            </Alert>
          )}
          {state.kind === "error" && (
            <Alert color="red">משהו השתבש: {state.detail}</Alert>
          )}
          <Group justify="center">
            <Anchor component={Link} to="/login" size="sm">
              חזרה למסך הכניסה
            </Anchor>
          </Group>
        </Stack>
      </Paper>
    </Container>
  );
}
